import {RoundedCalculations, SummaryCalculations} from "@client/calculations";
import {Macros, PieChart} from "@client/components/PieChart";
import * as typography from "@client/shared/typography";
import {classNames} from "@reactivated";
import * as React from "react";

const styles = {
    metrics: "flex",
    section: "flex-1 [&_th]:w-[53%] [&_th]:text-right",
    breakdown: "leading-[1.3] text-[11px] [&_th]:pb-2 [&_td]:pb-2",
} as const;

interface Props {
    size?: "condensed" | "share";
    calculations: RoundedCalculations & {summary: SummaryCalculations};
}

const doMyThang = (thang: string | null | undefined, unit = " g") =>
    thang != null ? `${thang}${unit}` : "N/A";

export const EntryMetrics = ({size, calculations}: Props) => {
    if (size == "share") {
        return (
            <>
                <div
                    style={{
                        width: 540,
                        maxWidth: "100%",
                        height: 440,
                        margin: "60px auto 0px auto",
                    }}
                >
                    <PieChart {...calculations.summary} size="share" />
                </div>

                <div className="-mt-12 mb-4 text-center text-4xl">
                    <span className="ml-2 font-extralight">
                        {calculations.calories} calories
                    </span>
                </div>
                <div className={classNames(styles.metrics, "flex gap-4")}>
                    <section className="flex-1">
                        <table className="w-full">
                            <tbody>
                                <tr className="text-3xl">
                                    <th className="w-1/2 py-2 pr-2 text-right font-normal">
                                        Protein
                                    </th>
                                    <td className="font-light">
                                        {doMyThang(calculations.protein)}
                                    </td>
                                </tr>
                                <tr className="text-3xl">
                                    <th className="w-1/2 py-2 pr-2 text-right font-normal">
                                        Fat
                                    </th>
                                    <td className="font-light">
                                        {doMyThang(calculations.fat)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                    <section className="flex-1">
                        <table className="w-full">
                            <tbody>
                                <tr className="text-3xl">
                                    <th className="w-1/2 py-2 pr-2 text-right font-normal">
                                        Carbs
                                    </th>
                                    <td className="font-light">
                                        {doMyThang(calculations.carbs)}
                                    </td>
                                </tr>
                                <tr className="text-3xl">
                                    <th className="w-1/2 py-2 pr-2 text-right font-normal">
                                        Fiber
                                    </th>
                                    <td className="font-light">
                                        {doMyThang(calculations.fiber)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                </div>
            </>
        );
    } else if (size == "condensed") {
        return (
            <>
                <Macros>
                    <PieChart {...calculations.summary} />
                </Macros>

                <div className="mt-4 text-center text-2xl">
                    <span className="ml-2 font-extralight">
                        {calculations.calories} calories
                    </span>
                </div>
                <div className="flex gap-4">
                    <section className="flex-1">
                        <table className="w-full">
                            <tbody>
                                <tr className="text-base">
                                    <th className="w-1/2 py-0.5 pr-2 text-right font-normal">
                                        Protein
                                    </th>
                                    <td className="font-light">
                                        {doMyThang(calculations.protein)}
                                    </td>
                                </tr>
                                <tr className="text-base">
                                    <th className="w-1/2 py-0.5 pr-2 text-right font-normal">
                                        Fat
                                    </th>
                                    <td className="font-light">
                                        {doMyThang(calculations.fat)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                    <section className="flex-1">
                        <table className="w-full">
                            <tbody>
                                <tr className="text-base">
                                    <th className="w-1/2 py-0.5 pr-2 text-right font-normal">
                                        Carbs
                                    </th>
                                    <td className="font-light">
                                        {doMyThang(calculations.carbs)}
                                    </td>
                                </tr>
                                <tr className="text-base">
                                    <th className="w-1/2 py-0.5 pr-2 text-right font-normal">
                                        Fiber
                                    </th>
                                    <td className="font-light">
                                        {doMyThang(calculations.fiber)}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                </div>
            </>
        );
    }
    return (
        <>
            <div className={styles.metrics}>
                <section className={styles.section}>
                    <typography.Table>
                        <tbody>
                            <tr>
                                <th>Calories</th>
                                <td>{calculations.calories} cal</td>
                            </tr>
                            <tr>
                                <th>Protein</th>
                                <td>{doMyThang(calculations.protein)}</td>
                            </tr>
                            <tr>
                                <th>Total Fat</th>
                                <td>{doMyThang(calculations.fat)}</td>
                            </tr>
                            <tr className={styles.breakdown}>
                                <th>
                                    Saturated Fat
                                    <br />
                                    Monounsaturated Fat
                                    <br />
                                    Polyunsaturated Fat
                                    <br />
                                    Trans Fat
                                    <br />
                                </th>
                                <td>
                                    {doMyThang(calculations.saturated_fat)}
                                    <br />
                                    {doMyThang(calculations.monounsaturated_fat)}
                                    <br />
                                    {doMyThang(calculations.polyunsaturated_fat)}
                                    <br />
                                    {doMyThang(calculations.trans_fat)}
                                    <br />
                                </td>
                            </tr>
                            <tr>
                                <th>Carbohydrates</th>
                                <td>{doMyThang(calculations.carbs)}</td>
                            </tr>
                            <tr className={styles.breakdown}>
                                <th>
                                    Total sugars
                                    <br />
                                    Added sugars
                                    <br />
                                    Fiber
                                    <br />
                                    Net carbs
                                    <br />
                                </th>
                                <td>
                                    {doMyThang(calculations.sugars)}
                                    <br />
                                    {doMyThang(calculations.added_sugars)}
                                    <br />
                                    {doMyThang(calculations.fiber)}
                                    <br />
                                    {doMyThang(calculations.net_carbs)}
                                </td>
                            </tr>
                        </tbody>
                    </typography.Table>
                </section>
                <section className={styles.section}>
                    <typography.Table>
                        <tbody>
                            <tr>
                                <th>Sodium</th>
                                <td>{doMyThang(calculations.sodium, " mg")}</td>
                            </tr>
                            <tr>
                                <th>Cholesterol</th>
                                <td>{doMyThang(calculations.cholesterol, " mg")}</td>
                            </tr>
                            <tr>
                                <th>Potassium</th>
                                <td>{doMyThang(calculations.potassium, " mg")}</td>
                            </tr>
                            <tr>
                                <th>Magnesium</th>
                                <td>{doMyThang(calculations.magnesium, " mg")}</td>
                            </tr>
                            <tr>
                                <th>Vitamin A</th>
                                <td>{doMyThang(calculations.vitamin_a_dv, " IU")}</td>
                            </tr>
                            <tr>
                                <th>Vitamin C</th>
                                <td>{doMyThang(calculations.vitamin_c_dv, " mg")}</td>
                            </tr>
                            <tr>
                                <th>Calcium</th>
                                <td>{doMyThang(calculations.calcium_dv, " mg")}</td>
                            </tr>
                            <tr>
                                <th>Iron</th>
                                <td>{doMyThang(calculations.iron_dv, " mg")}</td>
                            </tr>
                        </tbody>
                    </typography.Table>
                </section>
            </div>
            <Macros>
                <PieChart {...calculations.summary} />
            </Macros>
        </>
    );
};
